import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { BankAccountWithDetails } from '~/typings/API';

interface PinnedBankAccount {
  bankAccountId: string;
  order?: number;
}

interface UsePinBankAccountsReturn {
  pinnedAccounts: BankAccountWithDetails[];
  isPinned: (bankAccountId: string) => boolean;
  pinAccount: (bankAccountId: string, description: string) => Promise<void>;
  unpinAccount: (bankAccountId: string, description: string) => Promise<void>;
  togglePin: (bankAccountId: string, description: string) => Promise<void>;
  reorderAccounts: (newOrder: PinnedBankAccount[]) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

export const usePinBankAccounts = (): UsePinBankAccountsReturn => {
  const { currentUser, currentPlatform } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const queryClient = useQueryClient();

  const pinnedBankAccounts = currentUser?.getPlatformSettings?.pinnedBankAccounts ?? [];

  const sortedPinnedAccounts = useMemo(
    () => [...pinnedBankAccounts].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [pinnedBankAccounts]
  );

  const bankAccountQueries = useQueries({
    queries: sortedPinnedAccounts.map((pin) => ({
      queryKey: [currentPlatform?.id, 'bankAccount', pin.bankAccountId],
      queryFn: () => BankAccountRepository.get({ id: pin.bankAccountId }),
    })),
  });

  const isLoading = bankAccountQueries.some((query) => query.isLoading);
  const error = bankAccountQueries.find((query) => query.error)?.error ?? null;

  const pinnedAccounts = useMemo(() => {
    return bankAccountQueries
      .map((query) => query.data)
      .filter((account): account is BankAccountWithDetails => !!account);
  }, [bankAccountQueries]);

  const isPinned = useCallback(
    (bankAccountId: string): boolean => pinnedBankAccounts.some((pin) => pin.bankAccountId === bankAccountId),
    [pinnedBankAccounts]
  );

  const updateSettingsMutation = useMutation({
    mutationFn: async (newPinnedAccounts: PinnedBankAccount[]) => {
      if (!currentUser) {
        throw new Error('No current user');
      }
      return currentUser.updatePlatformSettings({
        pinnedBankAccounts: newPinnedAccounts,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankAccount'] });
    },
  });

  const pinAccount = useCallback(
    async (bankAccountId: string, description: string) => {
      const currentPinned = pinnedBankAccounts;
      const maxOrder = Math.max(...currentPinned.map((pin) => pin.order ?? 0), -1);

      const newPinnedAccount = {
        bankAccountId,
        order: maxOrder + 1,
      };

      try {
        await updateSettingsMutation.mutateAsync([...currentPinned, newPinnedAccount]);
        addSuccessNotification({
          content: `${description} pinned successfully`,
        });
      } catch (err) {
        addDangerNotification({
          content: err instanceof Error ? err.message : 'Failed to pin account',
        });
      }
    },
    [pinnedBankAccounts, updateSettingsMutation, addSuccessNotification, addDangerNotification]
  );

  const unpinAccount = useCallback(
    async (bankAccountId: string, description: string) => {
      const filteredPins = pinnedBankAccounts
        .filter((pin) => pin.bankAccountId !== bankAccountId)
        .map((pin, index) => ({ ...pin, order: index }));

      try {
        await updateSettingsMutation.mutateAsync(filteredPins);
        addSuccessNotification({
          content: `${description} unpinned successfully`,
        });
      } catch (err) {
        addDangerNotification({
          content: err instanceof Error ? err.message : 'Failed to unpin account',
        });
      }
    },
    [pinnedBankAccounts, updateSettingsMutation, addSuccessNotification, addDangerNotification]
  );

  const togglePin = useCallback(
    async (bankAccountId: string, description: string) => {
      if (isPinned(bankAccountId)) {
        await unpinAccount(bankAccountId, description);
      } else {
        await pinAccount(bankAccountId, description);
      }
    },
    [isPinned, pinAccount, unpinAccount]
  );

  const reorderAccounts = useCallback(
    async (newOrder: PinnedBankAccount[]) => {
      try {
        await updateSettingsMutation.mutateAsync(newOrder);
        addSuccessNotification({
          content: 'Pinned accounts reordered successfully',
        });
      } catch (err) {
        addDangerNotification({
          content: err instanceof Error ? err.message : 'Failed to reorder accounts',
        });
      }
    },
    [updateSettingsMutation, addSuccessNotification, addDangerNotification]
  );

  return {
    pinnedAccounts,
    isPinned,
    pinAccount,
    unpinAccount,
    togglePin,
    reorderAccounts,
    isLoading,
    error: error instanceof Error ? error : null,
  };
};
